import { InputBase, Typography } from '@mui/material'
import { styled } from '@mui/system'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { useCallback, useState, KeyboardEvent } from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import {
  TextMatchCondition,
  TextMatchMode,
  TextMatchQuantifier,
} from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import GroupedChipList from './GroupedChipList'

type Props = {
  title: string
  placeholder?: string
  value: TextMatchCondition[] | undefined
  onChange: (value: TextMatchCondition[] | undefined) => void
  includeQuantifier?: TextMatchQuantifier
  excludeQuantifier?: TextMatchQuantifier
  onToggleIncludeQuantifier?: () => void
  onToggleExcludeQuantifier?: () => void
}

const SearchConditionChipInput = ({
  title,
  placeholder,
  value,
  onChange,
  includeQuantifier,
  excludeQuantifier,
  onToggleIncludeQuantifier,
  onToggleExcludeQuantifier,
}: Props) => {
  const [inputValue, setInputValue] = useState('')

  const addChip = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const isExclude = trimmed.startsWith('-')
      const chipValue = isExclude ? trimmed.slice(1).trim() : trimmed
      if (!chipValue) return
      const newCondition: TextMatchCondition = {
        value: chipValue,
        mode: isExclude ? 'exclude' : 'include',
      }
      onChange([...(value ?? []), newCondition])
      setInputValue('')
    },
    [value, onChange]
  )

  const removeChip = useCallback(
    (index: number) => {
      if (!value) return
      const next = value.filter((_, i) => i !== index)
      onChange(next.length > 0 ? next : undefined)
    },
    [value, onChange]
  )

  const toggleChip = useCallback(
    (index: number) => {
      if (!value) return
      const next = value.map((c, i) =>
        i === index
          ? {
              ...c,
              mode: (c.mode === 'include'
                ? 'exclude'
                : 'include') as TextMatchMode,
            }
          : c
      )
      onChange(next)
    },
    [value, onChange]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        addChip(inputValue)
      }
    },
    [inputValue, addChip]
  )

  const clearAll = useCallback(() => {
    onChange(undefined)
    setInputValue('')
  }, [onChange])

  const hasChips = value && value.length > 0

  return (
    <div>
      <InputRow hasChips={!!hasChips}>
        <Typography
          sx={{
            color: colorPalette.monotone[4],
            minWidth: '80px',
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        <InputBase
          sx={{
            flex: 1,
            minWidth: '80px',
            padding: '0 8px',
            marginLeft: '20px',
            color: colorPalette.monotone[10],
          }}
          value={inputValue}
          placeholder={placeholder}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <ClearButton onClick={clearAll}>
          <ClearRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
        </ClearButton>
      </InputRow>
      {hasChips && (
        <GroupedChipList
          value={value}
          onToggle={toggleChip}
          onDelete={removeChip}
          includeQuantifier={includeQuantifier}
          excludeQuantifier={excludeQuantifier}
          onToggleIncludeQuantifier={onToggleIncludeQuantifier}
          onToggleExcludeQuantifier={onToggleExcludeQuantifier}
          sx={{
            padding: '6px 10px 6px 110px',
            borderLeft: `1px solid ${colorPalette.monotone[2]}`,
            borderRight: `1px solid ${colorPalette.monotone[2]}`,
            borderBottom: `1px solid ${colorPalette.monotone[2]}`,
            borderRadius: '0 0 4px 4px',
            backgroundColor: colorPalette.monotone[0],
          }}
        />
      )}
    </div>
  )
}

const InputRow = styled('div', {
  shouldForwardProp: prop => prop !== 'hasChips',
})<{ hasChips: boolean }>(({ hasChips }) => ({
  minHeight: '40px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: hasChips ? '4px 4px 0 0' : '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
}))

const ClearButton = styled('button')({
  minWidth: '20px',
  margin: '0 0 0 auto',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
})

export default SearchConditionChipInput
